<template>
  <div class="segment-tabs" role="tablist" :aria-label="ariaLabel">
    <div v-if="options.length" class="segment-tabs__slider" :style="sliderStyle"></div>

    <button
      v-for="item in options"
      :key="item.value"
      type="button"
      class="segment-tabs__tab"
      :class="{ 'is-active': item.value === value }"
      :disabled="item.disabled"
      role="tab"
      :aria-selected="item.value === value"
      :aria-disabled="item.disabled || undefined"
      :tabindex="item.value === value ? 0 : -1"
      @click="handleSelect(item.value, item.disabled)"
    >
      <i v-if="item.icon" class="segment-tabs__icon">
        <component :is="item.icon" />
      </i>
      <span class="segment-tabs__tab-text">
        <span class="segment-tabs__label">{{ item.label }}</span>
        <span v-if="item.badge != null && Number(item.badge) > 0" class="segment-tabs__badge" aria-hidden="true">
          {{ Number(item.badge) > 99 ? "99+" : item.badge }}
        </span>
      </span>
    </button>
  </div>
</template>

<script>
export default {
  name: "TabJkt",
  props: {
    value: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      default: () => []
    },
    ariaLabel: {
      type: String,
      default: "Tabs"
    }
  },

  data() {
    return {
      // 无需额外数据，使用计算属性即可
    };
  },

  computed: {
    optionCount() {
      return Math.max(this.options.length, 1);
    },

    activeIndex() {
      const currentIndex = this.options.findIndex((option) => option.value === this.value);
      return currentIndex >= 0 ? currentIndex : 0;
    },

    sliderStyle() {
      return {
        width: `calc((100% - (var(--segment-tabs-padding) * 2)) / ${this.optionCount})`,
        transform: `translateX(${this.activeIndex * 100}%)`
      };
    }
  },

  methods: {
    handleSelect(value, disabled = false) {
      if (disabled || value === this.value) return;

      // Vue 2 中使用 $emit 触发事件
      this.$emit("input", value); // 使用 input 事件支持 v-model
      this.$emit("change", value);
    }
  }
};
</script>

<style scoped>
.segment-tabs {
  --segment-tabs-height: 36px;
  --segment-tabs-padding: 4px;
  --segment-tabs-radius: 8px;
  --segment-tabs-bg: #f9fafb;
  --segment-tabs-active-bg: #ffffff;
  --segment-tabs-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  --segment-tabs-active-shadow: 0 1px 2px rgba(80, 110, 200, 0.14), 0 2px 8px rgba(15, 23, 42, 0.06);
  --segment-tabs-text-color: #595c5d;
  --segment-tabs-active-color: #2563eb;

  position: relative;
  display: flex;
  width: 100%;
  height: var(--segment-tabs-height);
  padding: var(--segment-tabs-padding);
  border-radius: var(--segment-tabs-radius);
  background: var(--segment-tabs-bg);
  box-shadow: var(--segment-tabs-shadow);
  overflow: hidden;
  box-sizing: border-box;
}

.segment-tabs__slider {
  position: absolute;
  top: var(--segment-tabs-padding);
  left: var(--segment-tabs-padding);
  height: calc(100% - (var(--segment-tabs-padding) * 2));
  border-radius: calc(var(--segment-tabs-radius) - 3px);
  background: var(--segment-tabs-active-bg);
  box-shadow: var(--segment-tabs-active-shadow);
  transition: transform 0.28s ease, box-shadow 0.28s ease;
}

.segment-tabs__tab {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: var(--segment-tabs-text-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: color 0.2s ease;
}

.segment-tabs__tab-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
}

.segment-tabs__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.segment-tabs__badge {
  flex-shrink: 0;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 8px;
  background: #f56c6c;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  text-align: center;
  box-sizing: border-box;
}

.segment-tabs__tab.is-active {
  color: var(--segment-tabs-active-color);
  font-weight: 500;
}

.segment-tabs__tab:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.segment-tabs__tab:focus-visible {
  outline: none;
  color: var(--segment-tabs-active-color);
}

.segment-tabs__icon {
  font-size: 16px;
}
</style>
